import { showToast } from "@lib/component/Toast/Toast";
import { ToastType } from "@lib/component/Toast/type";
import { useWindowSize } from "@lib/hook/useWindowSize";
import { AuthService } from "@module/auth/domain/service/auth";
import { authUIController } from "@module/auth/infras/util/authUIController";
import { formatCurrencyNumber } from "@module/auth/infras/util/formatCurrencyNumber";
import { HomeService } from "@module/home/domain/service/home";
import { keepPreviousData } from "@tanstack/react-query";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { FaCheck } from "react-icons/fa";
import { FiShoppingCart } from "react-icons/fi";

const ListService = () => {
    const router = useRouter();

    const { isLogin } = AuthService.useMe();
    const { deviceType } = useWindowSize();

    const { data } = HomeService.usePlans({
        fetcherOptions: {
            staleTime: 10000,
            gcTime: 10000,
            placeholderData: keepPreviousData
        }
    });

    const fixedPrice: any = {
        1: 249000,
        3: 229000,
        6: 199000,
        12: 159000
    }

    const savingPercentages = useMemo(() => {
        if (!data?.length) return [];

        const originalPrice = data[0].unitPrice;

        if (originalPrice === 0) return data.map(() => 0);

        return data.map((item) => Math.round(((originalPrice - item.unitPrice) / originalPrice) * 100));
    }, [data]);

    const getOrder = (planId: number) => {
        if (!isLogin) {
            showToast({
                type: ToastType.ERROR,
                description: "Vui lòng đăng nhập để đăng ký gói"
            });
            authUIController.handleCheckoutWithAuth(deviceType === "mobile", planId);
            return;
        }
        router.push(`/orders?planId=${planId}`);
    };

    return (
        <div className="mt-20 relative">
            <img
                src="/images/home/bg-service.png"
                alt=""
                className="h-[700px] w-full object-cover object-center absolute"
            />
            <div className="relative z-2">
                <div className="mx-auto flex-center border border-[#93C5FD] rounded-full w-[131px] h-8 gap-2">
                    <FiShoppingCart color="#20aeeb" className="size-[18px]" />
                    <div className="font-semibold leading-6 text-[#1FAEEB]">Gói dịch vụ</div>
                </div>
                <div className="mt-4 text-2xl leading-8 font-bold text-center text-[#1FAEEB]">
                    Đăng Ký Tài Khoản Pro
                </div>
                <div className="text-center leading-6 mt-3 text-[#4B5563] max-tab:mx-8 tab:max-w-[585px] mx-auto">
                    Giải pháp tối ưu hóa hiệu suất và an toàn dữ liệu. Đừng để lỡ bất kỳ khách hàng nào – Tạo tài khoản
                    để chúng tôi hỗ trợ bạn tối đa.
                </div>
                <div className="lap:overflow-y-auto lap:scrollbar-x-dark pb-5">
                    <div className="max-tab:hidden mt-10 mx-auto w-[590px] lap:w-[1210px] grid grid-cols-2 lap:grid-cols-4 gap-10">
                        {!!data &&
                            data.map((plan, index) => (
                                <div
                                    key={plan.id}
                                    className={clsx(
                                        "bg-white rounded-xl p-2 pb-4 relative border w-[272px] h-[556px] my-auto",
                                        plan.isBestSeller ? "border-[#2196F3]" : "border-[#F3F4F6]"
                                    )}
                                    style={{ boxShadow: "0px 0px 4px 0px #00000026" }}
                                >
                                    {plan.isBestSeller && (
                                        <div className="absolute left-7 top-[-23px] rounded-full overflow-hidden shadow p-1">
                                            <div className="absolute top-0 left-0 size-full bg-white opacity-50"/>
                                            <div className="relative z-10 w-[136px] h-10 rounded-full flex-center gap-1.5 bg-[#1FAEEB]">
                                                <div className="font-bold text-white text-sm leading-5">
                                                    Phổ biến nhất
                                                </div>
                                                <img src="/images/home/star-popular.svg" className="size-4" />
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className={clsx(
                                            "rounded-xl p-5 w-64 h-45 text-[#F9FAFB] flex flex-col gap-2 leading-6 font-medium",
                                            plan.isBestSeller && "pt-8"
                                        )}
                                        style={{
                                            backgroundImage: `url("/images/home/bg-service-card.png")`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center"
                                        }}
                                    >
                                        <div>Gói {plan.duration} tháng</div>
                                        <div className="flex flex-col">
                                            <span className="text-xl leading-9 font-bold line-through opacity-70">
                                                {formatCurrencyNumber(fixedPrice[plan.duration])} / Tháng
                                            </span>
                                            <span className="text-xl leading-9 font-bold">
                                                {formatCurrencyNumber(plan.unitPrice)} / Tháng
                                            </span>
                                        </div>
                                        {!!savingPercentages[index] && (
                                            <div className="font-base">Tiết kiệm {savingPercentages[index]}%</div>
                                        )}
                                    </div>
                                    <div className="h-px w-full bg-[#F3F4F6] my-4" />
                                    <div className="p-1.5 pr-2 flex flex-col gap-5">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-10.5 h-6 flex-center rounded-md font-bold text-sm leading-5 text-white"
                                                style={{
                                                    backgroundImage: `url("/images/home/service-pro.png")`,
                                                    backgroundSize: "cover",
                                                    backgroundPosition: "center"
                                                }}
                                            >
                                                PRO
                                            </div>
                                            <div className="font-medium text-sm leading-5 text-[#1F2937]">
                                                Nhân lịch không giới hạn
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                                <FaCheck color="#1FAEEB" className="w-3" />
                                            </div>
                                            <div className="font-medium text-sm leading-5 text-[#1F2937]">
                                                Dùng toàn bộ tính năng ứng dụng
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                                <FaCheck color="#1FAEEB" className="w-3" />
                                            </div>
                                            <div className="font-medium text-sm leading-5 text-[#1F2937]">
                                                Vuốt trả lời tin nhắn nhanh Zalo
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                                <FaCheck color="#1FAEEB" className="w-3" />
                                            </div>
                                            <div className="font-medium text-sm leading-5 text-[#1F2937]">
                                                Hỗ trợ kỹ thuật 24/7
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                                <FaCheck color="#1FAEEB" className="w-3" />
                                            </div>
                                            <div className="font-medium text-sm leading-5 text-[#1F2937]">
                                                Cập nhật tính năng mới liên tục
                                            </div>
                                        </div>
                                    </div>
                                    <div className="h-px w-full bg-[#F3F4F6] my-4" />
                                    <div
                                        onClick={() => getOrder(plan.id)}
                                        className="rounded-full w-full h-12 flex-center cursor-pointer bg-[#1FAEEB] text-lg leading-6 text-[#F9FAFB] font-semibold hover:opacity-90"
                                    >
                                        Đăng ký
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
                <div className="tab:hidden mt-8 mx-4 flex items-center flex-col gap-5">
                    <div
                        className="bg-white rounded-lg p-4 max-w-100 relative w-full overflow-hidden"
                        style={{ boxShadow: "0px 0px 4px 0px #00000026" }}
                    >
                        <img src="/images/home/bg-feature.svg" className="absolute size-full top-0 left-0 z-0" alt="" />
                        <div className="flex-center gap-2 relative z-10">
                            <img src="/images/home/pro.svg" className="w-10" alt="" />
                            <div className="font-bold leading-6 text-xl">Kích hoạt tính năng</div>
                        </div>
                        <div className="mt-5 flex flex-col gap-4 relative z-10">
                            <div className="flex items-center gap-2">
                                <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                    <FaCheck color="#1FAEEB" className="w-3" />
                                </div>
                                <div className="font-medium text-xs leading-4 text-[#1F2937]">
                                    Dùng toàn bộ tính năng ứng dụng
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                    <FaCheck color="#1FAEEB" className="w-3" />
                                </div>
                                <div className="font-medium text-xs leading-4 text-[#1F2937]">
                                    Không giới hạn tin nhắn tự động
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                    <FaCheck color="#1FAEEB" className="w-3" />
                                </div>
                                <div className="font-medium text-xs leading-4 text-[#1F2937]">
                                    Vuốt trả lời nhanh tin nhắn Zalo
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                    <FaCheck color="#1FAEEB" className="w-3" />
                                </div>
                                <div className="font-medium text-xs leading-4 text-[#1F2937]">
                                    Cập nhật tính năng mới liên tục
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-center rounded-full bg-[#F9FAFB] size-6">
                                    <FaCheck color="#1FAEEB" className="w-3" />
                                </div>
                                <div className="font-medium text-xs leading-4 text-[#1F2937]">Hỗ trợ kỹ thuật 24/7</div>
                            </div>
                        </div>
                    </div>
                    {!!data &&
                        data.map((plan, index) => (
                            <div
                                className={clsx(
                                    "rounded-lg p-4 max-w-100 w-full text-center bg-white overflow-hidden ",
                                    plan.isBestSeller ? "border border-[#1FAEEB]" : ""
                                )}
                                style={{ boxShadow: "0px 0px 16px 0px #00000026" }}
                                key={plan.id}
                            >
                                {plan.isBestSeller && (
                                    <div className="p-1.5 flex-center gap-2 bg-[#1FAEEB] font-bold text-white w-[calc(100%+32px)] -ml-4 -mt-4">
                                        Phổ biến nhất <img src="/images/home/star-popular.svg" />
                                    </div>
                                )}
                                <div
                                    className={clsx(
                                        "text-[#1FAEEB] font-bold text-xl leading-8",
                                        plan.isBestSeller ? "mt-4" : ""
                                    )}
                                >
                                    {plan.duration} Tháng
                                </div>
                                <div className="text-sm leading-5 text-[#6B7280]">
                                    {plan.duration === 1 && "Phù hợp cho trải nghiệm"}
                                    {plan.duration === 3 && "Phù hợp cho lựa chọn linh hoạt"}
                                    {plan.duration === 6 && "Phù hợp với công việc"}
                                    {plan.duration === 12 && "Đặc quyền giá hời nhất"}
                                </div>
                                <div className="  text-[#374151] mt-4">
                                    <div className="flex flex-col">
                                        <span className="text-xl leading-9 font-bold line-through opacity-70">
                                            {formatCurrencyNumber(fixedPrice[plan.duration])} / Tháng
                                        </span>
                                        <span className="text-xl leading-9 font-bold">
                                            {formatCurrencyNumber(plan.unitPrice)} / Tháng
                                        </span>
                                    </div>
                                </div>
                                {!!savingPercentages[index] && (
                                    <div className="text-[#6B7280] text-xs leading-4 mt-1">
                                        Tiết Kiệm {savingPercentages[index]}%
                                    </div>
                                )}
                                <div
                                    onClick={() => getOrder(plan.id)}
                                    className="mt-4 h-12 bg-[#1FAEEB] rounded-lg text-lg text-white leading-6 flex-center font-semibold"
                                >
                                    Đăng ký
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default ListService;
